/**
 * 应用相关工具函数
 */

import { isIOS, isAndroid } from './device';
import { getStorage } from './storage';

/**
 * 使用 rem 布局不要使用 text-size-adjust 改变字体效果，
 * 在 <meta name="viewport"> 之后使用该代码
 * @param {Number} designCSSWidth       设计基准CSS宽度，设备宽度 / dpr
 * @param {Number} baseFontSize         基准字体大小，当前 innerWidth 和 designCSSWidth 宽度相同时，html 的 font-size px 值
 * @param {Number} maxScale             最大的缩小放大倍数
 */
export function setFontSize(designCSSWidth?: number, baseFontSize?: number, maxScale?: number) {
  let winWidth = window.innerWidth;
  designCSSWidth = designCSSWidth || winWidth;
  baseFontSize = baseFontSize || winWidth / 10;
  maxScale = maxScale || 1.5;

  let w = window;
  let d = document;
  let on = 'addEventListener';
  let html = d.documentElement;
  let maxSize = maxScale * baseFontSize;

  function setRem() {
    // let isVertical = w.innerWidth < w.innerHeight;
    let width = w.innerWidth;
    // if (!isVertical) {
    //   width = w.innerHeight;
    // }
    let size = (baseFontSize! * width) / designCSSWidth!;
    html.style.fontSize = Math.min(size, maxSize) + 'px';
    (window as any)._fontSize = Math.min(size, maxSize);
  }

  let raf = w.requestAnimationFrame || (w as any).webkitRequestAnimationFrame;
  let limit = 10;

  function setRemFallback() {
    // 经测试不能保证第一次就拿到正确 innerWidth，所以设置一个延时（即次数限制）。
    limit--;
    if (limit && w.innerWidth > 500) {
      raf && raf(setRemFallback);
    } else {
      setRem();
      d.documentElement.style.display = 'block';
    }
  }

  if (w.innerWidth > 500 && isAndroid) {
    d.documentElement.style.display = 'none';
    /**
     * 部分机型在 WebView 内需要 load 之后才能获取正确 innerWidth。判断标准为其大于 500 的时候。
     * 先隐藏 HTML，拿到正确的 innerWidth 再显示。有问题的机器包括魅族 M1
     */
    if (!raf) {
      (w as any)[on]('load', setRemFallback, false);
    } else {
      raf(setRemFallback);
    }
  }
  // w[on]('resize', setRem, false);
  setRem();
}
export const setRootFontSize = ({ min = 320, max = 540 }) => {
  let deviceWidth = document.documentElement.clientWidth;
  let deviceHeight = document.documentElement.clientHeight;
  let width = deviceWidth > deviceHeight ? deviceHeight : deviceWidth;
  width = Math.min(Math.max(width, min), max);
  document.documentElement.style.fontSize = width / 10 + 'px';
};
/**
 * 版本号对比
 * 比较 sourceVersion 与 targetVersion。targetVersion 不传时，默认使用当前应用版本。
 * - sourceVersion > targetVersion 返回 -1
 * - sourceVersion = targetVersion 返回 0
 * - sourceVersion < targetVersion 返回 1
 * @param sourceVersion 源版本号，格式如 x.x.x
 * @param targetVersion 目标版本号，格式如 x.x.x；不传时使用当前应用版本
 */
export const compareVersions = (sourceVersion: string, targetVersion = getAppBaseInfo()?.app_version || '') => {
  if (!sourceVersion && !targetVersion) return 0;
  if (!sourceVersion) return 1;
  if (!targetVersion) return -1;

  const v1 = sourceVersion.split('.').map(Number);
  const v2 = targetVersion.split('.').map(Number);
  const maxLength = Math.max(v1.length, v2.length);

  for (let i = 0; i < maxLength; i++) {
    const num1 = i < v1.length ? v1[i] : 0;
    const num2 = i < v2.length ? v2[i] : 0;
    if (num1 > num2) {
      return -1;
    } else if (num1 < num2) {
      return 1;
    }
  }

  return 0;
};

/**
 * 获取应用基础信息
 * @returns 应用基础信息对象或null
 */
export const getAppBaseInfo = (() => {
  let cache: IAppBaseInfo | null | undefined = undefined;

  return (): IAppBaseInfo | null => {
    if (cache !== undefined) {
      return cache;
    }
    if ((window as any)._appBaseInfo !== undefined) {
      cache = (window as any)._appBaseInfo as IAppBaseInfo | null;
      return cache;
    }
    let ua = navigator.userAgent.replace(/\ +/g, '');
    let appBaseInfo: IAppBaseInfo | null = null;
    ua = ua.replace(/[\r\n]/g, '');
    const regex = /#webBaseInfo(.*?[^\\])#/;
    const match = regex.exec(ua);
    if (match && match[1]) {
      const extractedString = match[1];
      try {
        appBaseInfo = JSON.parse(extractedString);
      } catch (error) {
        console.log('解析ua的appVersion错误', ua);
      }
    } else {
      console.log('未找到匹配的字符串', ua);
    }

    // 缓存结果到内存
    cache = appBaseInfo;
    (window as any)._appBaseInfo = appBaseInfo;

    return cache;
  };
})();

/**
 * community 壳给内嵌 iframe 声明的浏览上下文名，用于判断当前页面是否被社区内嵌
 */
export const COMMUNITY_FRAME_NAME = 'docoi-web-community-frame';

/**
 * 是否处于嵌套浏览上下文（iframe）中
 */
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // 沙箱/跨域下访问 top 抛错，说明必然是被嵌套的文档
    return true;
  }
};

/**
 * 判断是否在web 版社区中内嵌
 * 依赖 community 壳在 iframe 上声明的 name（见 SafeWebFrame），
 * 浏览上下文名在 SPA 路由切换与整页跳转后都不会丢失
 * @returns 是否被社区内嵌
 */
export const isInWebCommunity = (() => {
  let cache: boolean | undefined;
  return (): boolean => {
    if (cache !== undefined) return cache;
    cache = isInIframe() && window.name === COMMUNITY_FRAME_NAME;
    return cache;
  };
})();

/**
 * 判断是否在DocoiApp
 * @returns 是否在DocoiApp中
 */
export const isInDocoiApp = (): boolean => {
  let isInAppCache: boolean = import.meta.env.DEV; // 缓存
  // let isInAppCache: boolean = false; // 缓存
  return (function (): boolean {
    if (isInAppCache) return isInAppCache;
    const appBaseInfo = getAppBaseInfo();
    isInAppCache = !!appBaseInfo;
    return isInAppCache;
  })();
};

/**
 * 判断是否在小程序环境
 */
export const isMiniProgram =
  /miniProgram/i.test(navigator.userAgent) || (window as any)._wxjs_environment === 'miniprogram';

export const isInGame = /#gameBaseInfo(.*?[^\\])#/.test(navigator.userAgent);

export const navbarHeight = () => {
  const paddingValue = Number(getStorage('navHeight'));
  return paddingValue;
};
export const statusHeight = () => {
  const paddingValue = Number(getStorage('statusHeight'));
  return paddingValue;
};
